import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { SignupConsent } from '../components/LegalDisclaimers';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AuthCallback() {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [showConsent, setShowConsent] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // CRITICAL: Use ref to prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    // Extract session_id from URL fragment
    const hash = window.location.hash;
    const params = new URLSearchParams(hash.substring(1));
    const extractedSessionId = params.get('session_id');

    if (!extractedSessionId) {
      toast.error('Authentication failed');
      navigate('/');
      return;
    }

    // Show consent screen first
    setSessionId(extractedSessionId);
    setShowConsent(true);
  }, [navigate]);

  const handleConsent = async () => {
    if (!consentChecked) {
      toast.error('Please accept the Terms of Service and Privacy Policy to continue');
      return;
    }

    setLoading(true);

    try {
      // Exchange session_id for session_token
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/session`,
        { session_id: sessionId },
        { withCredentials: true }
      );

      if (response.data.user) {
        toast.success('Successfully logged in!');
        // Navigate to dashboard with user data
        navigate('/dashboard', { state: { user: response.data.user }, replace: true });
      } else {
        throw new Error('No user data received');
      }
    } catch (error) {
      console.error('Auth error:', error);
      toast.error('Authentication failed. Please try again.');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  if (showConsent) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-sm p-8">
          <h2 className="font-secondary font-bold text-2xl text-slate-900 mb-6 text-center">
            Welcome to RendPleX
          </h2>
          
          <SignupConsent 
            checked={consentChecked}
            onChange={(e) => setConsentChecked(e.target.checked)}
          />

          <Button
            data-testid="continue-button"
            onClick={handleConsent}
            disabled={!consentChecked || loading}
            className="w-full mt-6 bg-primary hover:bg-[#143d24] text-white rounded-sm py-3"
          >
            {loading ? 'Processing...' : 'Continue to Dashboard'}
          </Button>

          <p className="text-xs text-slate-500 text-center mt-4">
            By continuing, you acknowledge that RendPleX provides analysis tools, not financial advice.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        <p className="mt-4 text-slate-600">Authenticating...</p>
      </div>
    </div>
  );
}
